import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './CourseManager.css';
import API_URL, { getUserPlatform } from '../api';
import { resolveAssetUrl } from '../utils/assetUrl';
import ConfirmModal from './ConfirmModal';
import LibrarySelectModal from './LibrarySelectModal';
import CourseStageModal from './CourseStageModal';

const STEP_TYPES = [
    { value: 'text', label: '📖 Текстовий урок' },
    { value: 'video', label: '🎥 Відео-лекція' },
    { value: 'pdf', label: '📄 PDF-матеріал' },
    { value: 'Quiz', label: '📝 Квіз' },
    { value: 'DeskTemplate', label: '🖥️ Сервірування' },
    { value: 'MultiDeskTest', label: '📚 Комбінована сервірування' },
    { value: 'GameScenario', label: '🎮 Інтерактивна гра' },
    { value: 'PizzaTemplate', label: '🍕 Конструктор піци' },
    { value: 'ComplexTest', label: '🧩 Комплексний тест' },
    { value: 'flashcards', label: '🎴 Флешкартки' },
    { value: 'hotspots', label: '🎯 Активні зони' }
];

const PRACTICE_TYPES = ['Quiz', 'DeskTemplate', 'MultiDeskTest', 'GameScenario', 'PizzaTemplate', 'ComplexTest'];

function CourseManager() {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('list'); // 'list' | 'edit'
    const [search, setSearch] = useState('');
    const [filterActive, setFilterActive] = useState('all');
    const [user, setUser] = useState(null);
    const [cities, setCities] = useState([]);
    const [stages, setStages] = useState([]);
    const [resources, setResources] = useState({
        Quiz: [], DeskTemplate: [], MultiDeskTest: [],
        GameScenario: [], PizzaTemplate: [], ComplexTest: [], CertificateTemplate: []
    });
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, idToDelete: null });
    const [libraryPicker, setLibraryPicker] = useState({ isOpen: false, mIdx: null, sIdx: null, allowedType: '', cardIdx: undefined, attachmentMode: false });
    const [isStageModalOpen, setIsStageModalOpen] = useState(false);
    const [stepEditor, setStepEditor] = useState({ isOpen: false, mIdx: null, sIdx: null });

        const [editingId, setEditingId] = useState(null);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [coverImage, setCoverImage] = useState('');
    const [targetCity, setTargetCity] = useState('');
    const [certificateTemplateId, setCertificateTemplateId] = useState('');
    const [stageId, setStageId] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [modules, setModules] = useState([]);
    const [departmentsList, setDepartmentsList] = useState([]);
    const [selectedPositions, setSelectedPositions] = useState([]);

    useEffect(() => {
        fetchUser();
        fetchCities();
        fetchResources();
        fetchCourses();
        fetchStages();
        fetchDepartments();
    }, []);

    function fetchDepartments() {
        axios.get(`${API_URL}/departments`)
            .then(r => setDepartmentsList(r.data))
            .catch(() => {});
    }

    function fetchStages() {
        axios.get(`${API_URL}/course-stages`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }).then(r => setStages(r.data)).catch(() => {});
    }

    function fetchUser() {
        axios.get(`${API_URL}/auth/me`).then(r => setUser(r.data)).catch(() => {});
    }

    function fetchCities() {
        axios.get(`${API_URL}/cities${getUserPlatform() ? `?platform=${getUserPlatform()}` : ''}`)
            .then(r => setCities(r.data)).catch(() => {});
    }

    function fetchResources() {
        axios.get(`${API_URL}/courses/available-resources`)
            .then(r => setResources(r.data)).catch(() => {});
    }

    async function fetchCourses() {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/courses`);
            setCourses(res.data);
        } catch (err) {
            console.error('Error fetching courses:', err);
        } finally {
            setLoading(false);
        }
    }

    function resetForm() {
        setEditingId(null);
        setTitle('');
        setDescription('');
        setCoverImage('');
        setTargetCity('');
        setCertificateTemplateId('');
        setStageId('');
        setIsActive(true);
        setModules([]);
        setSelectedPositions([]);
    }

    function startCreate() {
        resetForm();
        setView('edit');
    }

    function startEdit(course) {
        setEditingId(course._id);
        setTitle(course.title);
        setDescription(course.description || '');
        setCoverImage(course.coverImage || '');
        setTargetCity(course.city || '');
        setCertificateTemplateId(course.certificateTemplateId?._id || course.certificateTemplateId || '');
        setStageId(course.stageId?._id || course.stageId || '');
        setIsActive(course.isActive);
        setModules(course.modules || []);
        setSelectedPositions(course.positions || []);
        setView('edit');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    function addModule() {
        setModules(prev => [...prev, {
            title: `Модуль ${prev.length + 1}`,
            description: '',
            steps: []
        }]);
    }

    function removeModule(mIdx) {
        setModules(prev => prev.filter((_, idx) => idx !== mIdx));
    }

    function updateModule(mIdx, field, value) {
        setModules(prev => prev.map((m, idx) => idx === mIdx ? { ...m, [field]: value } : m));
    }

    function moveModule(mIdx, direction) {
        setModules(prev => {
            const next = [...prev];
            const t = mIdx + direction;
            if (t < 0 || t >= next.length) return prev;
            [next[mIdx], next[t]] = [next[t], next[mIdx]];
            return next;
        });
    }

    function addStep(mIdx) {
        setModules(prev => prev.map((m, idx) => {
            if (idx !== mIdx) return m;
            return {
                ...m,
                steps: [...m.steps, {
                    title: `Крок ${m.steps.length + 1}`,
                    type: 'text',
                    contentHtml: '',
                    mediaUrl: '',
                    resourceId: '',
                    resourceModel: null,
                    minScoreToPass: 80,
                    isRequired: true
                }]
            };
        }));
    }

    function removeStep(mIdx, sIdx) {
        setModules(prev => prev.map((m, idx) => {
            if (idx !== mIdx) return m;
            return { ...m, steps: m.steps.filter((_, s) => s !== sIdx) };
        }));
    }

    function updateStep(mIdx, sIdx, field, value) {
        setModules(prev => prev.map((m, idx) => {
            if (idx !== mIdx) return m;
            const newSteps = m.steps.map((step, s) => {
                if (s !== sIdx) return step;
                const updated = { ...step, [field]: value };
                if (field === 'type') {
                    updated.resourceModel = PRACTICE_TYPES.includes(value) ? value : null;
                    updated.resourceId = '';
                }
                return updated;
            });
            return { ...m, steps: newSteps };
        }));
    }

    function moveStep(mIdx, sIdx, direction) {
        setModules(prev => prev.map((m, idx) => {
            if (idx !== mIdx) return m;
            const steps = [...m.steps];
            const t = sIdx + direction;
            if (t < 0 || t >= steps.length) return m;
            [steps[sIdx], steps[t]] = [steps[t], steps[sIdx]];
            return { ...m, steps };
        }));
    }

    function openStepEditor(mIdx, sIdx) {
        setStepEditor({ isOpen: true, mIdx, sIdx });
    }

    function openLibraryPicker(mIdx, sIdx, type, cardIdx) {
        setLibraryPicker({ isOpen: true, mIdx, sIdx, allowedType: type, cardIdx, attachmentMode: false });
    }

    function openAttachmentPicker(mIdx, sIdx) {
        setLibraryPicker({ isOpen: true, mIdx, sIdx, allowedType: 'pdf', cardIdx: undefined, attachmentMode: true });
    }

    function handleSelectLibraryFile(url) {
        const { mIdx, sIdx, cardIdx, attachmentMode } = libraryPicker;
        if (attachmentMode) {
            const step = modules[mIdx].steps[sIdx];
            const existing = step.attachments || [];
            updateStep(mIdx, sIdx, 'attachments', [...existing, { url, title: url.split('/').pop() }]);
        } else if (cardIdx !== undefined) {
            const step = modules[mIdx].steps[sIdx];
            let cards = [];
            try { cards = step.contentHtml ? JSON.parse(step.contentHtml) : []; } catch (e) {}
            if (cards[cardIdx]) {
                cards[cardIdx].image = url;
                updateStep(mIdx, sIdx, 'contentHtml', JSON.stringify(cards));
            }
        } else {
            updateStep(mIdx, sIdx, 'mediaUrl', url);
        }
        setLibraryPicker({ isOpen: false, mIdx: null, sIdx: null, allowedType: '', cardIdx: undefined, attachmentMode: false });
    }

    function removeAttachment(mIdx, sIdx, attIdx) {
        const step = modules[mIdx].steps[sIdx];
        updateStep(mIdx, sIdx, 'attachments', (step.attachments || []).filter((_, i) => i !== attIdx));
    }

    async function handleSave() {
        if (!title.trim()) { alert('Введіть назву курсу'); return; }
        if (modules.length === 0) { alert('Додайте хоча б один модуль'); return; }
        for (let m = 0; m < modules.length; m++) {
            const mod = modules[m];
            if (!mod.title.trim()) { alert(`Введіть назву для модуля ${m + 1}`); return; }
            if (mod.steps.length === 0) { alert(`Додайте хоча б один крок у модуль "${mod.title}"`); return; }
            for (let s = 0; s < mod.steps.length; s++) {
                const st = mod.steps[s];
                if (!st.title.trim()) { alert(`Введіть назву кроку ${s + 1} в модулі "${mod.title}"`); return; }
                if (PRACTICE_TYPES.includes(st.type) && !st.resourceId) {
                    alert(`Виберіть тест для кроку "${st.title}" в модулі "${mod.title}"`);
                    return;
                }
            }
        }
        try {
            const payload = {
                title: title.trim(),
                description: description.trim(),
                coverImage: coverImage.trim(),
                city: ['superadmin', 'admin', 'trainer'].includes(user?.role) ? targetCity : undefined,
                certificateTemplateId: certificateTemplateId || null,
                stageId: stageId || null,
                isActive,
                modules,
                positions: selectedPositions
            };
            if (editingId) {
                await axios.put(`${API_URL}/courses/${editingId}`, payload);
            } else {
                await axios.post(`${API_URL}/courses`, payload);
            }
            resetForm();
            setView('list');
            fetchCourses();
        } catch (err) {
            alert(`Помилка: ${err.response?.data?.error || err.message}`);
        }
    }

    async function handleConfirmDelete() {
        const id = confirmModal.idToDelete;
        if (!id) return;
        setConfirmModal({ isOpen: false, idToDelete: null });
        try {
            await axios.delete(`${API_URL}/courses/${id}`);
            if (editingId === id) resetForm();
            fetchCourses();
        } catch (err) {
            alert('Помилка при видаленні');
        }
    }

    async function handleCoverUpload(e) {
        const file = e.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('title', `Обкладинка курсу: ${title || file.name}`);
        try {
            const res = await axios.post(`${API_URL}/library/upload`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${localStorage.getItem('token')}`
                }
            });
            setCoverImage(res.data.url);
        } catch (err) {
            alert('Помилка завантаження: ' + (err.response?.data?.error || err.message));
        }
    }

    const filteredCourses = courses.filter(c => {
        const matchSearch = !search || c.title.toLowerCase().includes(search.toLowerCase());
        const matchActive = filterActive === 'all' ||
            (filterActive === 'active' && c.isActive) ||
            (filterActive === 'inactive' && !c.isActive);
        return matchSearch && matchActive;
    });

    const totalSteps = (mod) => mod.steps.length;
    const stepIcon = (type) => {
        const t = STEP_TYPES.find(s => s.value === type);
        return t ? t.label.split(' ')[0] : '📄';
    };

    if (view === 'list') {
        return (
            <div className="course-manager-container">
                <header className="course-manager-header">
                    <div>
                        <h1>📖 Курси навчання</h1>
                        <p>Створюйте та керуйте інтерактивними курсами для персоналу</p>
                    </div>
                    <div className="header-actions">
                        <button className="btn-primary" onClick={startCreate}>
                            ➕ Створити курс
                        </button>
                    </div>
                </header>

                <div className="cm-toolbar">
                    <input
                        type="text"
                        className="cm-search"
                        placeholder="🔍 Пошук курсів..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                    <div className="cm-filters">
                        {['all', 'active', 'inactive'].map(f => (
                            <button
                                key={f}
                                className={`cm-filter-btn ${filterActive === f ? 'active' : ''}`}
                                onClick={() => setFilterActive(f)}
                            >
                                {f === 'all' ? 'Всі' : f === 'active' ? '● Активні' : '○ Неактивні'}
                            </button>
                        ))}
                    </div>
                </div>

                {loading ? (
                    <div className="placeholder-view">Завантаження...</div>
                ) : filteredCourses.length === 0 ? (
                    <div className="cm-empty">
                        <div className="cm-empty-icon">📚</div>
                        <h3>{search ? 'Нічого не знайдено' : 'Ще немає курсів'}</h3>
                        <p>{search ? 'Спробуйте інший запит' : 'Натисніть "Створити курс", щоб додати перший'}</p>
                    </div>
                ) : (
                    <div className="cm-course-grid">
                        {filteredCourses.map(course => (
                            <div key={course._id} className={`cm-course-card ${!course.isActive ? 'inactive' : ''}`}>
                                {course.coverImage && (
                                    <div className="cm-course-cover"
                                        style={{ backgroundImage: `url(${course.coverImage.startsWith('/api') ? `${API_URL.replace('/api', '')}${course.coverImage}` : course.coverImage})` }}
                                    />
                                )}
                                <div className="cm-course-body">
                                    <div className="cm-course-header">
                                        <h3>{course.title}</h3>
                                        <span className={`cm-badge-status ${course.isActive ? 'active' : 'inactive'}`}>
                                            {course.isActive ? 'Активний' : 'Неактивний'}
                                        </span>
                                    </div>
                                    <p className="cm-course-desc">{course.description || 'Опис відсутній'}</p>
                                    <div className="cm-course-meta">
                                        {course.city && <span className="cm-meta-tag city">📍 {course.city}</span>}
                                        {course.positions && course.positions.length > 0 && (
                                            <span className="cm-meta-tag positions" title={course.positions.map(p => {
                                                const found = departmentsList.find(d => d.key === p);
                                                return found ? found.name : p;
                                            }).join(', ')}>
                                                🏢 {course.positions.map(p => {
                                                    const found = departmentsList.find(d => d.key === p);
                                                    return found ? found.name : p;
                                                }).slice(0, 2).join(', ')}{course.positions.length > 2 ? '...' : ''}
                                            </span>
                                        )}
                                        <span className="cm-meta-tag modules">📚 {course.modules?.length || 0} модулів</span>
                                        <span className="cm-meta-tag steps">📝 {(course.modules || []).reduce((a, m) => a + (m.steps?.length || 0), 0)} кроків</span>
                                        {course.certificateTemplateId && <span className="cm-meta-tag cert">🎓 Сертифікат</span>}
                                    </div>
                                </div>
                                <div className="cm-course-actions">
                                    <button className="cm-btn-icon edit" onClick={() => startEdit(course)} title="Редагувати">✏️</button>
                                    <button className="cm-btn-icon delete" onClick={() => setConfirmModal({ isOpen: true, idToDelete: course._id })} title="Видалити">🗑️</button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <ConfirmModal
                    isOpen={confirmModal.isOpen}
                    title="Видалення курсу"
                    message="Ви впевнені? Прогрес студентів за цим курсом буде видалено."
                    confirmText="Видалити"
                    onConfirm={handleConfirmDelete}
                    onCancel={() => setConfirmModal({ isOpen: false, idToDelete: null })}
                />
            </div>
        );
    }

    return (
        <div className="course-manager-container">
            <header className="course-manager-header">
                <div>
                    <h1>{editingId ? '✏️ Редагування курсу' : '➕ Новий курс'}</h1>
                    <p>{editingId ? 'Змініть параметри та структуру курсу' : 'Заповніть інформацію про курс'}</p>
                </div>
                <div className="header-actions">
                    <button className="btn-ghost" onClick={() => { resetForm(); setView('list'); }}>
                        ← До списку
                    </button>
                    <button className="btn-primary" onClick={handleSave}>
                        💾 {editingId ? 'Оновити' : 'Створити'}
                    </button>
                </div>
            </header>

            <div className="cm-editor-layout">
                <div className="cm-editor-main">
                    <div className="cm-section">
                        <h2>⚙️ Основне</h2>
                        <div className="cm-form-grid">
                            <div className="cm-field">
                                <label>Назва курсу *</label>
                                <input 
                                    type="text" 
                                    value={title} 
                                    onChange={e => setTitle(e.target.value)} 
                                    placeholder="Наприклад: Вступний курс офіціанта" 
                                    required 
                                />
                            </div>
                            <div className="cm-field">
                                <label>Обкладинка (URL або файл)</label>
                                <div className="cm-cover-box">
                                    {coverImage && (
                                        <div className="cm-cover-preview-bar">
                                            <img 
                                                src={coverImage.startsWith('/api') ? `${API_URL.replace('/api', '')}${coverImage}` : coverImage} 
                                                alt="Обкладинка" 
                                                className="cm-cover-thumb" 
                                                onError={(e) => { e.target.style.display = 'none'; }}
                                            />
                                            <span className="cm-cover-url-text" title={coverImage}>{coverImage}</span>
                                            <button type="button" className="cm-cover-remove-btn" onClick={() => setCoverImage('')} title="Видалити">
                                                🗑️
                                            </button>
                                        </div>
                                    )}
                                    <div className="cm-cover-input-group">
                                        <input 
                                            type="text" 
                                            value={coverImage} 
                                            onChange={e => setCoverImage(e.target.value)} 
                                            placeholder="Вставте URL зображення або завантажте..." 
                                        />
                                        <input type="file" accept="image/*" id="cover-file" onChange={handleCoverUpload} style={{ display: 'none' }} />
                                        <label htmlFor="cover-file" className="cm-upload-btn">📁 Файл</label>
                                    </div>
                                </div>
                            </div>
                            <div className="cm-field span-2">
                                <label>Опис</label>
                                <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Опишіть, чого навчиться співробітник..." rows={2} />
                            </div>
                            {['superadmin', 'admin', 'trainer'].includes(user?.role) && (
                                <div className="cm-field">
                                    <label>📍 Місто</label>
                                    <select value={targetCity} onChange={e => setTargetCity(e.target.value)}>
                                        <option value="">Всі міста</option>
                                        {cities.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
                                    </select>
                                </div>
                            )}
                            <div className="cm-field span-2">
                                <label style={{ color: 'var(--text-main)' }}>🏢 Обмеження за посадами (якщо не вибрано жодної, доступно всім)</label>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginTop: '6px', padding: '10px', background: 'var(--subtle-bg)', borderRadius: '6px', border: '1px solid var(--border)', color: 'var(--text-main)' }}>
                                    {departmentsList.map(dept => {
                                        const isChecked = selectedPositions.includes(dept.key);
                                        return (
                                            <label key={dept._id} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none', fontSize: '0.9rem', color: 'var(--text-main)' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={e => {
                                                        if (e.target.checked) {
                                                            setSelectedPositions(prev => [...prev, dept.key]);
                                                        } else {
                                                            setSelectedPositions(prev => prev.filter(k => k !== dept.key));
                                                        }
                                                    }}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                                <span>{dept.name}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="cm-field">
                                <label>🎓 Сертифікат</label>
                                <select value={certificateTemplateId} onChange={e => setCertificateTemplateId(e.target.value)}>
                                    <option value="">Не видавати</option>
                                    {resources.CertificateTemplate.map(t => (
                                        <option key={t._id} value={t._id}>{t.title}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="cm-field">
                                <label>📂 Етап</label>
                                <div className="cm-field-row">
                                    <select value={stageId} onChange={e => setStageId(e.target.value)} style={{ flex: 1 }}>
                                        <option value="">Без етапу</option>
                                        {stages.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                                    </select>
                                    <button className="cm-icon-btn" onClick={() => setIsStageModalOpen(true)} title="Налаштувати етапи">⚙️</button>
                                </div>
                            </div>
                            <div className="cm-field">
                                <label className="cm-toggle">
                                    <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
                                    <span className="cm-toggle-slider" />
                                    <span>Активний курс</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="cm-section">
                        <div className="cm-section-header">
                            <h2>📚 Модулі та кроки</h2>
                            <button className="cm-btn-add" onClick={addModule}>➕ Додати модуль</button>
                        </div>
                        {modules.length === 0 ? (
                            <div className="cm-empty-modules">
                                Курс порожній. Натисніть «Додати модуль», щоб почати.
                            </div>
                        ) : (
                            <div className="cm-modules">
                                {modules.map((mod, mIdx) => (
                                    <div key={mIdx} className="cm-module">
                                        <div className="cm-module-head">
                                            <div className="cm-module-move">
                                                <button disabled={mIdx === 0} onClick={() => moveModule(mIdx, -1)}>▲</button>
                                                <button disabled={mIdx === modules.length - 1} onClick={() => moveModule(mIdx, 1)}>▼</button>
                                            </div>
                                            <input className="cm-module-title"
                                                value={mod.title}
                                                onChange={e => updateModule(mIdx, 'title', e.target.value)}
                                                placeholder="Назва модуля..."
                                            />
                                            <span className="cm-module-count">{totalSteps(mod)} кроків</span>
                                            <button className="cm-btn-icon delete" onClick={() => removeModule(mIdx)} title="Видалити модуль">🗑️</button>
                                        </div>
                                        <input className="cm-module-desc"
                                            value={mod.description}
                                            onChange={e => updateModule(mIdx, 'description', e.target.value)}
                                            placeholder="Опис модуля (необов'язково)..."
                                        />
                                        <div className="cm-steps">
                                            {mod.steps.map((step, sIdx) => {
                                                const isPractice = PRACTICE_TYPES.includes(step.type);
                                                const hasComplexEditor = step.type === 'hotspots' || step.type === 'flashcards';
                                                return (
                                                    <div key={sIdx} className="cm-step">
                                                        <div className="cm-step-head">
                                                            <div className="cm-step-move">
                                                                <button disabled={sIdx === 0} onClick={() => moveStep(mIdx, sIdx, -1)}>▲</button>
                                                                <button disabled={sIdx === mod.steps.length - 1} onClick={() => moveStep(mIdx, sIdx, 1)}>▼</button>
                                                            </div>
                                                            <span className="cm-step-num">{sIdx + 1}</span>
                                                            <span className="cm-step-icon">{stepIcon(step.type)}</span>
                                                            <input className="cm-step-title"
                                                                value={step.title}
                                                                onChange={e => updateStep(mIdx, sIdx, 'title', e.target.value)}
                                                                placeholder="Назва кроку..."
                                                            />
                                                            <span className="cm-step-type">{STEP_TYPES.find(t => t.value === step.type)?.label || step.type}</span>
                                                            {hasComplexEditor && (
                                                                <button className="cm-btn-edit-step" onClick={() => openStepEditor(mIdx, sIdx)} title="Редагувати вміст">
                                                                    ✏️
                                                                </button>
                                                            )}
                                                            <button className="cm-btn-icon delete" onClick={() => removeStep(mIdx, sIdx)} title="Видалити крок">×</button>
                                                        </div>
                                                        <div className="cm-step-body">
                                                            <div className="cm-step-field half">
                                                                <label>Тип</label>
                                                                <select value={step.type} onChange={e => updateStep(mIdx, sIdx, 'type', e.target.value)}>
                                                                    {STEP_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                                                </select>
                                                            </div>
                                                            {step.type === 'text' && (
                                                                <div className="cm-step-field wide">
                                                                    <label>Вміст уроку</label>
                                                                    <textarea value={step.contentHtml} onChange={e => updateStep(mIdx, sIdx, 'contentHtml', e.target.value)}
                                                                        placeholder="Текст уроку..." rows={3} />
                                                                </div>
                                                            )}
                                                            {(step.type === 'video' || step.type === 'pdf') && (
                                                                <div className="cm-step-field wide">
                                                                    <label>URL медіа</label>
                                                                    <div className="cm-field-row">
                                                                        <input value={step.mediaUrl} onChange={e => updateStep(mIdx, sIdx, 'mediaUrl', e.target.value)}
                                                                            placeholder="https://..." style={{ flex: 1 }} />
                                                                        <button className="cm-icon-btn" onClick={() => openLibraryPicker(mIdx, sIdx, step.type)}>📂</button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {step.type === 'pdf' && (
                                                                <div className="cm-step-field wide cm-attachments-section">
                                                                    <label className="cm-attachments-label">📎 Додаткові PDF</label>
                                                                    {(step.attachments || []).length > 0 && (
                                                                        <div className="cm-attachments-list">
                                                                            {step.attachments.map((att, attIdx) => (
                                                                                <div key={attIdx} className="cm-attachment-item">
                                                                                    <span className="cm-attachment-name">📄 {att.title || att.url.split('/').pop()}</span>
                                                                                    <a href={att.url} target="_blank" rel="noopener noreferrer" className="cm-attachment-link">Відкрити</a>
                                                                                    <button className="cm-attachment-remove" onClick={() => removeAttachment(mIdx, sIdx, attIdx)} title="Видалити">×</button>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                    <button className="cm-attachment-add-btn" onClick={() => openAttachmentPicker(mIdx, sIdx)}>
                                                                        📂 Додати PDF з бібліотеки
                                                                    </button>
                                                                </div>
                                                            )}
                                                            {step.type === 'hotspots' && (
                                                                <div className="cm-step-field wide">
                                                                    <label>Фонове зображення</label>
                                                                    <div className="cm-field-row">
                                                                        <input value={step.mediaUrl} onChange={e => updateStep(mIdx, sIdx, 'mediaUrl', e.target.value)}
                                                                            placeholder="URL зображення..." style={{ flex: 1 }} />
                                                                        <button className="cm-icon-btn" onClick={() => openLibraryPicker(mIdx, sIdx, 'image')}>📂</button>
                                                                    </div>
                                                                    <div className="cm-hotspot-hint">
                                                                        🎯 Зони налаштовуються в редакторі ({(() => { try { return JSON.parse(step.contentHtml || '[]').filter(h => h.isCorrect).length; } catch { return 0; }})()} помилок)
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {step.type === 'flashcards' && (
                                                                <div className="cm-step-field wide">
                                                                    <label>Флешкартки</label>
                                                                    <div className="cm-hotspot-hint">
                                                                        🎴 {(() => { try { return JSON.parse(step.contentHtml || '[]').length; } catch { return 0; }})()} карток
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {isPractice && (
                                                                <>
                                                                    <div className="cm-step-field half">
                                                                        <label>Тест для оцінювання</label>
                                                                        <select value={step.resourceId} onChange={e => updateStep(mIdx, sIdx, 'resourceId', e.target.value)}>
                                                                            <option value="">-- Виберіть --</option>
                                                                            {(resources[step.type] || []).map(r => (
                                                                                <option key={r._id} value={r._id}>{r.title}</option>
                                                                            ))}
                                                                        </select>
                                                                    </div>
                                                                    <div className="cm-step-field half">
                                                                        <label>Прохідний бал (%)</label>
                                                                        <input type="number" min="0" max="100" value={step.minScoreToPass}
                                                                            onChange={e => updateStep(mIdx, sIdx, 'minScoreToPass', Number(e.target.value))} />
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <button className="cm-btn-add-step" onClick={() => addStep(mIdx)}>➕ Додати крок</button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {stepEditor.isOpen && (
                <StepEditorModal
                    step={modules[stepEditor.mIdx]?.steps[stepEditor.sIdx]}
                    mIdx={stepEditor.mIdx}
                    sIdx={stepEditor.sIdx}
                    onUpdate={(field, value) => updateStep(stepEditor.mIdx, stepEditor.sIdx, field, value)}
                    onClose={() => setStepEditor({ isOpen: false, mIdx: null, sIdx: null })}
                    openLibraryPicker={(type, cardIdx) => openLibraryPicker(stepEditor.mIdx, stepEditor.sIdx, type, cardIdx)}
                    librarySelect={libraryPicker}
                    onLibrarySelect={handleSelectLibraryFile}
                    onLibraryClose={() => setLibraryPicker({ isOpen: false, mIdx: null, sIdx: null, allowedType: '', cardIdx: undefined })}
                    resolveAssetUrl={resolveAssetUrl}
                />
            )}

            <LibrarySelectModal
                isOpen={libraryPicker.isOpen}
                allowedType={libraryPicker.allowedType}
                onSelect={handleSelectLibraryFile}
                onClose={() => setLibraryPicker({ isOpen: false, mIdx: null, sIdx: null, allowedType: '', cardIdx: undefined, attachmentMode: false })}
            />

            <CourseStageModal
                isOpen={isStageModalOpen}
                onClose={() => setIsStageModalOpen(false)}
                onRefreshStages={fetchStages}
            />

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                title="Видалення курсу"
                message="Ви впевнені? Прогрес студентів буде видалено."
                confirmText="Видалити"
                onConfirm={handleConfirmDelete}
                onCancel={() => setConfirmModal({ isOpen: false, idToDelete: null })}
            />
        </div>
    );
}

function StepEditorModal({ step, mIdx, sIdx, onUpdate, onClose, openLibraryPicker, librarySelect, onLibrarySelect, onLibraryClose, resolveAssetUrl }) {
    if (!step) return null;

    if (step.type === 'flashcards') {
        let cards = [];
        try { cards = step.contentHtml ? JSON.parse(step.contentHtml) : []; } catch (e) { cards = []; }
        if (!Array.isArray(cards)) cards = [];

        const updateCards = (newCards) => {
            onUpdate('contentHtml', JSON.stringify(newCards));
        };

        return (
            <div className="cm-modal-overlay" onClick={onClose}>
                <div className="cm-modal cm-modal-wide" onClick={e => e.stopPropagation()}>
                    <div className="cm-modal-header">
                        <h2>🎴 Флешкартки — {step.title}</h2>
                        <button className="cm-modal-close" onClick={onClose}>×</button>
                    </div>
                    <div className="cm-modal-body">
                        <div className="fc-toolbar">
                            <span>{cards.length} карток</span>
                            <button className="cm-btn-add" onClick={() => updateCards([...cards, { term: '', definition: '', image: '' }])}>
                                ➕ Додати картку
                            </button>
                        </div>
                        {cards.length === 0 && (
                            <div className="fc-empty">
                                <p>Ще немає карток. Натисніть "Додати картку", щоб створити першу.</p>
                            </div>
                        )}
                        {cards.map((card, cIdx) => (
                            <div key={cIdx} className="fc-card">
                                <div className="fc-card-head">
                                    <span className="fc-card-num">#{cIdx + 1}</span>
                                    <button className="fc-remove" onClick={() => updateCards(cards.filter((_, i) => i !== cIdx))}
                                        title="Видалити картку">✕</button>
                                </div>
                                <div className="fc-card-body">
                                    <div className="fc-card-fields">
                                        <label className="fc-label">Термін (лицьова сторона)</label>
                                        <input placeholder="наприклад: Апероль" value={card.term || ''}
                                            onChange={e => {
                                                const next = [...cards];
                                                next[cIdx] = { ...next[cIdx], term: e.target.value };
                                                updateCards(next);
                                            }} />
                                        <label className="fc-label">Визначення (зворотна сторона)</label>
                                        <textarea placeholder="наприклад: італійський аперитив на основі просеко, гіркого апельсину та ревеню"
                                            value={card.definition || ''}
                                            onChange={e => {
                                                const next = [...cards];
                                                next[cIdx] = { ...next[cIdx], definition: e.target.value };
                                                updateCards(next);
                                            }} rows={2} />
                                        <label className="fc-label">URL зображення</label>
                                        <div className="fc-img-row">
                                            <input placeholder="https://example.com/photo.jpg" value={card.image || ''}
                                                onChange={e => {
                                                    const next = [...cards];
                                                    next[cIdx] = { ...next[cIdx], image: e.target.value };
                                                    updateCards(next);
                                                }} />
                                            <button className="cm-icon-btn" onClick={() => openLibraryPicker('image', cIdx)} title="Вибрати з бібліотеки">📂</button>
                                        </div>
                                    </div>
                                    <div className="fc-card-preview">
                                        <div className="fc-preview-label">Попередній перегляд</div>
                                        <div className="fc-preview-card">
                                            <div className="fc-preview-front">
                                                {card.image && (
                                                    <div className="fc-preview-img" style={{ backgroundImage: `url(${resolveAssetUrl ? resolveAssetUrl(card.image) : card.image})` }} />
                                                )}
                                                <div className="fc-preview-term">{card.term || 'Термін'}</div>
                                            </div>
                                            <div className="fc-preview-back">
                                                {card.term && <div className="fc-preview-back-term">{card.term}</div>}
                                                <div className="fc-preview-def">{card.definition || 'Визначення'}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (step.type === 'hotspots') {
        let hotspots = [];
        try { hotspots = step.contentHtml ? JSON.parse(step.contentHtml) : []; } catch (e) { hotspots = []; }
        if (!Array.isArray(hotspots)) hotspots = [];

        const updateHotspots = (next) => {
            onUpdate('contentHtml', JSON.stringify(next));
        };

        const addHotspot = (e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const x = ((e.clientX - rect.left) / rect.width) * 100;
            const y = ((e.clientY - rect.top) / rect.height) * 100;
            updateHotspots([...hotspots, { x: Math.max(0, Math.min(92, x - 4)), y: Math.max(0, Math.min(92, y - 4)), width: 8, height: 8, title: 'Нова зона', isCorrect: true }]);
        };

        return (
            <div className="cm-modal-overlay" onClick={onClose}>
                <div className="cm-modal cm-modal-wide" onClick={e => e.stopPropagation()}>
                    <div className="cm-modal-header">
                        <h2>🎯 Активні зони — {step.title}</h2>
                        <button className="cm-modal-close" onClick={onClose}>×</button>
                    </div>
                    <div className="cm-modal-body">
                        <div className="hs-toolbar">
                            <span>✅ Правильних: {hotspots.filter(h => h.isCorrect).length} | ❌ Помилок: {hotspots.filter(h => !h.isCorrect).length}</span>
                            <div>
                                <input type="number" id="hs-count" min="0" max="50" defaultValue={hotspots.filter(h => !h.isCorrect).length} style={{ width: 60, marginRight: 8 }} />
                                <button className="cm-btn-sm" onClick={() => {
                                    const count = parseInt(document.getElementById('hs-count')?.value || '0', 10);
                                    if (count <= 0) return;
                                    const existing = hotspots.filter(h => !h.isCorrect);
                                    for (let i = 0; i < count; i++) {
                                        existing.push({ x: 5 + Math.random() * 82, y: 5 + Math.random() * 82, width: 6 + Math.random() * 8, height: 6 + Math.random() * 8, title: 'Помилка ' + (existing.length + 1), isCorrect: false });
                                    }
                                    updateHotspots(existing);
                                }}>🎲 Згенерувати</button>
                                <button className="cm-btn-sm" onClick={() => updateHotspots(hotspots.filter(h => h.isCorrect))}>🗑️ Очистити помилки</button>
                            </div>
                        </div>
                        <div className="hs-canvas" style={{ backgroundImage: `url(${resolveAssetUrl(step.mediaUrl)})` }} onClick={addHotspot}>
                            {hotspots.map((h, i) => (
                                <div key={i} className={`hs-zone ${h.isCorrect ? 'correct' : 'wrong'}`}
                                    style={{ left: `${h.x}%`, top: `${h.y}%`, width: `${h.width}%`, height: `${h.height}%` }}
                                    onClick={e => e.stopPropagation()}>
                                    {i + 1}
                                </div>
                            ))}
                        </div>
                        <table className="hs-table">
                            <thead>
                                <tr><th>#</th><th>Назва</th><th>X</th><th>Y</th><th>W</th><th>H</th><th>Помилка?</th><th></th></tr>
                            </thead>
                            <tbody>
                                {hotspots.map((h, i) => (
                                    <tr key={i}>
                                        <td>{i + 1}</td>
                                        <td><input value={h.title} onChange={e => {
                                            const next = [...hotspots];
                                            next[i] = { ...next[i], title: e.target.value };
                                            updateHotspots(next);
                                        }} /></td>
                                        <td><input type="number" value={Math.round(h.x)} onChange={e => {
                                            const next = [...hotspots]; next[i] = { ...next[i], x: Number(e.target.value) }; updateHotspots(next);
                                        }} /></td>
                                        <td><input type="number" value={Math.round(h.y)} onChange={e => {
                                            const next = [...hotspots]; next[i] = { ...next[i], y: Number(e.target.value) }; updateHotspots(next);
                                        }} /></td>
                                        <td><input type="number" value={Math.round(h.width)} onChange={e => {
                                            const next = [...hotspots]; next[i] = { ...next[i], width: Number(e.target.value) }; updateHotspots(next);
                                        }} /></td>
                                        <td><input type="number" value={Math.round(h.height)} onChange={e => {
                                            const next = [...hotspots]; next[i] = { ...next[i], height: Number(e.target.value) }; updateHotspots(next);
                                        }} /></td>
                                        <td><input type="checkbox" checked={!!h.isCorrect} onChange={e => {
                                            const next = [...hotspots]; next[i] = { ...next[i], isCorrect: e.target.checked }; updateHotspots(next);
                                        }} /></td>
                                        <td><button className="fc-remove" onClick={() => updateHotspots(hotspots.filter((_, idx) => idx !== i))}>×</button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    return null;
}

export default CourseManager;
